/**
 * @fileoverview Accept-invitation card.
 * @description Reads the `token` query param from the /invitation/accept URL, validates it (GET
 * /identity/invitations/{token}/validate) and renders: a spinner while that's in flight, the
 * set-password form once it succeeds, or an error card otherwise. The backend answers every
 * unusable link — unknown, expired, already used, revoked — with the same appCode 1040, so all of
 * those share one "link no longer valid" card (offering Log In, for someone who already set their
 * password); only a rate-limited or unavailable check (1041/1042, or a network failure) gets a
 * separate "try again" card, since the link itself may be fine. A visitor who already has a
 * session never reaches this component — InvitationRoute redirects them upstream. The decision is
 * a standalone, hook-free function (resolveInvitationScreen) so it's separately unit-testable.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import logoUrl from '@/assets/images/login/logo.png'
import InvitationErrorCard from '@/features/auth/components/InvitationErrorCard'
import SetPasswordForm from '@/features/auth/components/SetPasswordForm'
import { useValidateInvitation } from '@/features/auth/hooks/useValidateInvitation'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

import type { ApiError } from '@/shared/types/http.types'

type InvitationScreen =
  | { kind: 'loading' }
  | { kind: 'setPassword' }
  | { kind: 'error'; variant: 'invalidLink' | 'unavailable' }

interface ResolveInvitationScreenParams {
  hasToken: boolean
  isPending: boolean
  isSuccess: boolean
  error: ApiError | null
}

/**
 * @description Decides which screen the validate query's current state calls for. Kept as a
 * standalone, hook-free function so it can be unit tested without rendering the component.
 * @param {ResolveInvitationScreenParams} params - The token presence and query state.
 * @returns {InvitationScreen} Which screen to render.
 */
function resolveInvitationScreen(params: ResolveInvitationScreenParams): InvitationScreen {
  const { hasToken, isPending, isSuccess, error } = params

  if (!hasToken) {
    return { kind: 'error', variant: 'invalidLink' }
  }

  if (isPending) {
    return { kind: 'loading' }
  }

  if (isSuccess) {
    return { kind: 'setPassword' }
  }

  const isLinkProblem =
    error?.code === ERROR_CODES.INVITATION_INVALID || error?.code === ERROR_CODES.VALIDATION_ERROR

  return { kind: 'error', variant: isLinkProblem ? 'invalidLink' : 'unavailable' }
}

/**
 * @description Renders the spinner shown while the link is being checked.
 */
function LoadingCard() {
  const { t } = useTranslation()

  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div className="mt-8 flex w-full items-center justify-center rounded-[18px] border border-primary/10 bg-white p-10 shadow-card">
        <span
          className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary"
          role="status"
          aria-label={t('acceptInvitation.loading')}
        />
      </div>
    </div>
  )
}

/**
 * @description Renders the logo/card chrome (mirrors AuthCard/ForgotPasswordCard) around the
 * set-password form for a still-valid link.
 * @param {{ token: string }} props - The token being redeemed.
 */
function SetPasswordCard({ token }: { token: string }) {
  const { t } = useTranslation()

  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div className="mt-8 w-full rounded-[18px] border border-primary/10 bg-white p-6 shadow-card sm:p-8 lg:p-10">
        <h1 className="text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]">
          {t('acceptInvitation.title')}
        </h1>
        <p className="mt-1.5 text-[15px] text-slate sm:text-base">
          {t('acceptInvitation.subtitle')}
        </p>
        <SetPasswordForm token={token} />
      </div>
    </div>
  )
}

/**
 * @component
 */
function AcceptInvitationCard() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const validateQuery = useValidateInvitation(token)

  const screen = resolveInvitationScreen({
    hasToken: token.length > 0,
    isPending: validateQuery.isPending,
    isSuccess: validateQuery.isSuccess,
    error: validateQuery.error,
  })

  switch (screen.kind) {
    case 'loading':
      return <LoadingCard />
    case 'setPassword':
      return <SetPasswordCard token={token} />
    case 'error':
      return (
        <InvitationErrorCard
          variant={screen.variant}
          onRetry={() => void validateQuery.refetch()}
        />
      )
  }
}

export default AcceptInvitationCard
